import React from 'react';
import { motion } from 'framer-motion';
interface SyllableBlocksProps {
  word: string;
  syllables: string[];
  types: ('open' | 'closed')[];
  /**
   * Index (single) or indices (array) of syllable block(s) to highlight.
   * Accepts both shapes for didactic flexibility:
   *  - `0`        → highlight first syllable only
   *  - `[0, 1]`   → highlight multiple syllables (e.g. open + gesloten side-by-side)
   */
  highlight?: number | number[];
  label?: string;
  /** 'stimulus' = compact visual only (shown in-question), 'support' = full educational panel (shown after answer) */
  mode?: 'stimulus' | 'support';
}
const isSyllableHighlighted = (highlight: number | number[] | undefined, index: number): boolean => {
  if (highlight === undefined) return false;
  if (Array.isArray(highlight)) return highlight.includes(index);
  return highlight === index;
};
/**
 * SyllableBlocks - Word syllable breakdown visual
 *
 * Shows word broken into syllables with open/closed indication.
 * Used for SPR (Spelling Regels) module sound group questions.
 *
 * stimulus mode: compact — word + syllable blocks only
 * support mode: full educational panel with explanations
 */
export function SyllableBlocks({
  word,
  syllables,
  types,
  highlight,
  label,
  mode = 'support'
}: SyllableBlocksProps) {
  // Find vowels in each syllable
  const getVowels = (syllable: string): number[] => {
    const vowels = ['a', 'e', 'i', 'o', 'u'];
    const positions: number[] = [];
    syllable.split('').forEach((char, index) => {
      if (vowels.includes(char.toLowerCase())) {
        positions.push(index);
      }
    });
    return positions;
  };
  // ── STIMULUS MODE: compact visual only ──────────────────────────────────
  if (mode === 'stimulus') {
    return <div className="w-full max-w-md mx-auto" data-id="element-14423" data-mp="234yb3wh00a4p">
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 border border-green-200" data-id="element-14424" data-mp="vq3bnxd9ynw9">
          {/* Header - compact */}
          {label && <div className="mb-3 text-center" data-id="element-14425" data-mp="t4m5vverzzr8">
              <span className="text-xs font-bold text-green-800" data-id="element-14426" data-mp="t5x9nv8817ic">{label}</span>
            </div>}

          {/* Complete Word - compact */}
          <div className="mb-3 text-center" data-id="element-14427" data-mp="y1clkhmo4tc4">
            <span className="text-lg font-black text-slate-800" data-id="element-14428" data-mp="j77p1uoxx8ye">{word}</span>
          </div>

          {/* Syllable Blocks - compact */}
          <div className="flex justify-center items-center gap-2 flex-wrap" data-id="element-14429" data-mp="o3ofivjm3zen">
            {syllables.map((syllable, index) => {
            const type = types[index];
            const isHighlighted = isSyllableHighlighted(highlight, index);
            const vowelPositions = getVowels(syllable);
            return <motion.div key={index} initial={{
              scale: 0,
              y: 10
            }} animate={{
              scale: 1,
              y: 0
            }} transition={{
              delay: index * 0.1,
              type: 'spring',
              stiffness: 300
            }} className="flex flex-col items-center" data-id="element-14430" data-mp="du2acvds8tv" data-mpi={index}>
                  
                  <div className={`
                    relative px-4 py-2.5 rounded-xl border-3 shadow-md
                    ${type === 'open' ? 'bg-gradient-to-br from-green-400 to-emerald-500 border-green-600' : 'bg-gradient-to-br from-red-400 to-rose-500 border-red-600'}
                    ${isHighlighted ? 'ring-3 ring-yellow-300 scale-105' : ''}
                  `} data-id="element-14431" data-mp="38yz2y5dbai5u">
                    
                    <div className="flex gap-0.5" data-id="element-14432" data-mp="3rvrxe4y7tp1r">
                      {syllable.split('').map((char, charIndex) => {
                    const isVowel = vowelPositions.includes(charIndex);
                    const isLastLetter = charIndex === syllable.length - 1;
                    return <span key={charIndex} className={`
                              text-xl font-black
                              ${isVowel ? 'text-white drop-shadow-md' : 'text-white/90'}
                              ${isLastLetter ? isVowel ? 'ring-2 ring-green-200 bg-green-600/40 rounded px-0.5' : 'ring-2 ring-red-200 bg-red-600/40 rounded px-0.5' : ''}
                            `} data-id="element-14433" data-mp="idxuznsw980" data-mpi={charIndex}>
                            
                            {char}
                          </span>;
                  })}
                    </div>
                    <div className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-white border-2 border-current flex items-center justify-center shadow-sm" data-id="element-14434" data-mp="12ds8d2n7jkzx">
                      <span className="text-[10px] font-bold" data-id="element-14435" data-mp="amks0hn9q5bx">
                        {type === 'open' ? '○' : '●'}
                      </span>
                    </div>
                  </div>
                  <div className={`
                    mt-1.5 px-2 py-0.5 rounded text-[10px] font-bold
                    ${type === 'open' ? 'bg-green-100 text-green-800 border border-green-200' : 'bg-red-100 text-red-800 border border-red-200'}
                  `} data-id="element-14436" data-mp="jm7pwtgsgt08">
                    
                    {type === 'open' ? 'Open' : 'Gesloten'}
                  </div>
                </motion.div>;
          })}
          </div>
        </div>
      </div>;
  }
  // ── SUPPORT MODE: full educational panel (original behavior) ────────────
  return <div className="w-full max-w-3xl mx-auto" data-id="element-14437" data-mp="3fkgnz6k57zb2">
      <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 md:p-8 border-2 border-green-200 shadow-lg" data-id="element-14438" data-mp="3asypcp5leat9">
        {/* Header */}
        <div className="mb-6 text-center" data-id="element-14439" data-mp="35tlyzufiillx">
          <div className="inline-flex items-center gap-3 bg-white/90 rounded-xl px-5 py-3 border border-green-200 shadow-sm" data-id="element-14440" data-mp="31saa5r4gq4es">
            <span className="text-2xl" data-id="element-14441" data-mp="2wsfgxgx7t2qk">🔤</span>
            <div className="text-left" data-id="element-14442" data-mp="2wvtkutlj1k04">
              <div className="text-sm font-bold text-green-900" data-id="element-14443" data-mp="2rtkpxrp3g7vd">
                {label || 'Lettergrepen Verdeling'}
              </div>
              <div className="text-xs text-green-600 font-semibold" data-id="element-14444" data-mp="2mx410va2lph8">
                Open en Gesloten Lettergrepen
              </div>
            </div>
          </div>
        </div>

        {/* Complete Word */}
        <div className="mb-6 text-center" data-id="element-14445" data-mp="l1tniej5i8fj">
          <div className="inline-flex flex-col items-center gap-2 bg-white/80 rounded-xl px-6 py-4 border border-slate-200 shadow-sm" data-id="element-14446" data-mp="2fjmrepsdroac">
            <div className="text-xs font-bold text-slate-500 uppercase tracking-wide" data-id="element-14447" data-mp="2kcfr2z8zs6fk">
              Heel Woord
            </div>
            <div className="text-3xl font-black text-slate-800" data-id="element-14448" data-mp="25k47288k60hr">{word}</div>
          </div>
        </div>

        {/* Syllable Blocks */}
        <div className="mb-6" data-id="element-14449" data-mp="2yactica4n725">
          <div className="flex justify-center items-center gap-3 flex-wrap" data-id="element-14450" data-mp="2yb5pqjbgme61">
            {syllables.map((syllable, index) => {
            const type = types[index];
            const isHighlighted = isSyllableHighlighted(highlight, index);
            const vowelPositions = getVowels(syllable);
            return <motion.div key={index} initial={{
              scale: 0,
              y: 20
            }} animate={{
              scale: 1,
              y: 0
            }} transition={{
              delay: index * 0.15,
              type: 'spring',
              stiffness: 300
            }} className="flex flex-col items-center" data-id="element-14451" data-mp="2t9q5gth3wbop" data-mpi={index}>
                  
                  {/* Syllable Block */}
                  <div className={`
                    relative px-6 py-4 rounded-2xl border-4 shadow-xl
                    transition-all duration-300
                    ${type === 'open' ? 'bg-gradient-to-br from-green-400 to-emerald-500 border-green-600' : 'bg-gradient-to-br from-red-400 to-rose-500 border-red-600'}
                    ${isHighlighted ? 'ring-4 ring-yellow-300 scale-110' : ''}
                  `} data-id="element-14452" data-mp="15dinl4b2vmga">
                    
                    {/* Letters */}
                    <div className="flex gap-1" data-id="element-14453" data-mp="11dq96az06m30">
                      {syllable.split('').map((char, charIndex) => {
                    const isVowel = vowelPositions.includes(charIndex);
                    const isLastLetter = charIndex === syllable.length - 1;
                    return <span key={charIndex} className={`
                              text-2xl md:text-3xl font-black
                              ${isVowel ? 'text-white drop-shadow-lg' : 'text-white/90'}
                              ${isLastLetter ? isVowel ? 'ring-2 ring-green-200 bg-green-600/40 rounded px-1' : 'ring-2 ring-red-200 bg-red-600/40 rounded px-1' : ''}
                            `} data-id="element-14454" data-mp="3xkse6v02obv" data-mpi={charIndex}>
                            
                            {char}
                          </span>;
                  })}
                    </div>

                    {/* Type indicator */}
                    <div className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white border-2 border-current flex items-center justify-center shadow-md" data-id="element-14455" data-mp="6andt7ekis5r">
                      <span className="text-xs font-bold" data-id="element-14456" data-mp="b4hue1yvmr0m">
                        {type === 'open' ? '○' : '●'}
                      </span>
                    </div>
                  </div>

                  {/* Label */}
                  <div className={`
                    mt-2 px-3 py-1 rounded-lg text-xs font-bold
                    ${type === 'open' ? 'bg-green-100 text-green-800 border border-green-200' : 'bg-red-100 text-red-800 border border-red-200'}
                  `} data-id="element-14457" data-mp="p1n6ojp6om4b">
                    
                    {type === 'open' ? 'Open (lange klank)' : 'Gesloten (korte klank)'}
                  </div>
                </motion.div>;
          })}
          </div>
        </div>

        {/* Educational Explanations */}
        <div className="space-y-4" data-id="element-14458" data-mp="2saymgtobtvme">
          {/* What do you see? */}
          <div className="bg-white/80 rounded-xl p-5 border border-blue-100 shadow-sm" data-id="element-14459" data-mp="2o8twc4x37h4i">
            <div className="flex items-start gap-3" data-id="element-14460" data-mp="2ob7pg887ij9f">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0" data-id="element-14461" data-mp="2jh3ihoyxyb8c">
                <span className="text-blue-600 text-lg" data-id="element-14462" data-mp="2ei8xcgdi4al2">👀</span>
              </div>
              <div data-id="element-14463" data-mp="3bzhh3qs3q18h">
                <h4 className="text-sm font-bold text-blue-900 mb-2" data-id="element-14464" data-mp="3bzzamfndq1cp">
                  Wat zie je?
                </h4>
                <div className="text-sm text-slate-700 leading-relaxed space-y-2" data-id="element-14465" data-mp="375msqa1jwy32">
                  <p data-id="element-14466" data-mp="22yzs4kc93ffx">
                    Het woord <strong data-id="element-14467" data-mp="1xzmvj7vkuu05">{word}</strong> bestaat uit{' '}
                    <strong data-id="element-14468" data-mp="1y17quzppugz2">{syllables.length}</strong> lettergreep
                    {syllables.length > 1 ? 'en' : ''}:{' '}
                    {syllables.map((s, i) => <span key={i} data-id="element-14469" data-mp="27xb1ktymvnxm" data-mpi={i}>
                        <strong className={types[i] === 'open' ? 'text-green-700' : 'text-red-700'} data-id="element-14470" data-mp="27w9cvmop32xf">
                        
                          {s}
                        </strong>
                        {i < syllables.length - 1 ? ' + ' : ''}
                      </span>)}
                  </p>
                  <div className="pt-2 border-t border-blue-100 space-y-1" data-id="element-14471" data-mp="2vmvmhieckv9j">
                    <p className="flex items-center gap-2" data-id="element-14472" data-mp="2qpd8hdlh3puo">
                      <span className="w-4 h-4 rounded bg-green-500" data-id="element-14473" data-mp="34o37wzvrm1m7"></span>
                      <span data-id="element-14474" data-mp="34m0iagyiyai0">
                        <strong data-id="element-14475" data-mp="2zpa2zvujnixn">Groen (○):</strong> Open lettergreep = lange
                        klank (eindigt op een klinker)
                      </span>
                    </p>
                    <p className="flex items-center gap-2" data-id="element-14476" data-mp="3j8z8y9pk50vo">
                      <span className="w-4 h-4 rounded bg-red-500" data-id="element-14477" data-mp="3o8k9upoafjb1"></span>
                      <span data-id="element-14478" data-mp="3aavmnb6r002t">
                        <strong data-id="element-14479" data-mp="3a9ki395haoan">Rood (●):</strong> Gesloten lettergreep = korte
                        klank (eindigt op een medeklinker)
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Open vs Closed explanation */}
          <div className="bg-emerald-50/80 rounded-xl p-5 border border-emerald-200 shadow-sm" data-id="element-14480" data-mp="35cu54uoz08tg">
            <div className="flex items-start gap-3" data-id="element-14481" data-mp="2qka2dv5qg3cj">
              <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-14482" data-mp="2vm96x8d7l6b2">
                <span className="text-emerald-600 text-lg" data-id="element-14483" data-mp="2hlyei0w93jns">📖</span>
              </div>
              <div data-id="element-14484" data-mp="2mj82dc3zrn9i">
                <h4 className="text-sm font-bold text-emerald-900 mb-2" data-id="element-14485" data-mp="2mjq58rl5szqe">
                  Open vs Gesloten Lettergrepen
                </h4>
                <div className="text-sm text-slate-700 leading-relaxed space-y-3" data-id="element-14486" data-mp="ndulbbq9hmjy">
                  <div className="bg-green-50 p-3 rounded-lg border border-green-200" data-id="element-14487" data-mp="iehp40di334p">
                    <p className="font-semibold text-green-800 mb-1" data-id="element-14488" data-mp="ic59htv7t4ut">
                      Open lettergreep (○):
                    </p>
                    <p data-id="element-14489" data-mp="1b3f6m19w0vwe">
                      Eindigt op een <strong data-id="element-14490" data-mp="1b2dv5voym7e5">klinker</strong> (a, e, i, o, u).
                      De klinker klinkt <strong data-id="element-14491" data-mp="165dopjo772mi">lang</strong>.
                    </p>
                    <p className="text-xs mt-1 text-green-700" data-id="element-14492" data-mp="121z74okm2e6y">
                      Voorbeeld: <strong data-id="element-14493" data-mp="124bjmm7lnwcm">ja-ger</strong> (ja = open, lange 'a')
                    </p>
                  </div>
                  <div className="bg-red-50 p-3 rounded-lg border border-red-200" data-id="element-14494" data-mp="3msg6nhddidb5">
                    <p className="font-semibold text-red-800 mb-1" data-id="element-14495" data-mp="3mqd8ga22hdvc">
                      Gesloten lettergreep (●):
                    </p>
                    <p data-id="element-14496" data-mp="1xcdmm5999qt4">
                      Eindigt op een <strong data-id="element-14497" data-mp="1x9hlk8242m0g">medeklinker</strong>. De klinker
                      klinkt <strong data-id="element-14498" data-mp="22bgqhmdi1qzi">kort</strong>.
                    </p>
                    <p className="text-xs mt-1 text-red-700" data-id="element-14499" data-mp="26cioy0zldz0f">
                      Voorbeeld: <strong data-id="element-14500" data-mp="26afsay3xp7mq">bak-ker</strong> (bak = gesloten, korte
                      'a')
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Spelling rule */}
          <div className="bg-indigo-50/80 rounded-xl p-5 border border-indigo-200 shadow-sm" data-id="element-14501" data-mp="2l03tlzc486ag">
            <div className="flex items-start gap-3" data-id="element-14502" data-mp="2pzzz630go5s4">
              <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0" data-id="element-14503" data-mp="2pyf4mde5cmu9">
                <span className="text-indigo-600 text-lg" data-id="element-14504" data-mp="vm8jalrjgpo8">✍️</span>
              </div>
              <div data-id="element-14505" data-mp="vol91l3jg4bu">
                <h4 className="text-sm font-bold text-indigo-900 mb-2" data-id="element-14506" data-mp="znk7cmurl2ub">
                  Spellingregel
                </h4>
                <div className="text-sm text-slate-700 leading-relaxed space-y-2" data-id="element-14507" data-mp="4cm4k20zktge">
                  <p data-id="element-14508" data-mp="clhjsztn9xq">
                    <strong data-id="element-14509" data-mp="baa9j763k1v">Korte klank (gesloten):</strong> Schrijf meestal{' '}
                    <strong data-id="element-14510" data-mp="ec39uicbt5bu">twee medeklinkers</strong> na de klinker.
                  </p>
                  <p className="text-xs text-indigo-700 bg-indigo-50 p-2 rounded border border-indigo-200" data-id="element-14511" data-mp="9c8jcfwgqhrb">
                    Voorbeeld: bak-ker (korte 'a' → dubbele k), zit-ten (korte
                    'i' → dubbele t)
                  </p>
                  <p className="pt-2 border-t border-indigo-200" data-id="element-14512" data-mp="3cva6aam5ntrx">
                    <strong data-id="element-14513" data-mp="3rjeqjj0uwdct">Lange klank (open):</strong> Schrijf meestal{' '}
                    <strong data-id="element-14514" data-mp="3mrwm2kzmjun3">één medeklinker</strong> na de klinker.
                  </p>
                  <p className="text-xs text-indigo-700 bg-indigo-50 p-2 rounded border border-indigo-200" data-id="element-14515" data-mp="2z22cccalqm72">
                    Voorbeeld: ja-ger (lange 'a' → enkele g), bo-men (lange 'o'
                    → enkele m)
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Tip */}
          <div className="bg-amber-50/80 rounded-xl p-5 border border-amber-200 shadow-sm" data-id="element-14516" data-mp="1ecot5642090u">
            <div className="flex items-start gap-3" data-id="element-14517" data-mp="10l1oahw1ps6g">
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0" data-id="element-14518" data-mp="10ifgch58tanp">
                <span className="text-amber-600 text-lg" data-id="element-14519" data-mp="1sbepgjj1u4o5">💡</span>
              </div>
              <div data-id="element-14520" data-mp="1s9lkr9rzxuis">
                <h4 className="text-sm font-bold text-amber-900 mb-2" data-id="element-14521" data-mp="1xa989lh0nt89">
                  Handige tip
                </h4>
                <p className="text-sm text-slate-700 leading-relaxed" data-id="element-14522" data-mp="1j9op2o76vve8">
                  Spreek het woord hardop uit! Hoor je een{' '}
                  <strong data-id="element-14523" data-mp="1o801n6ovck05">korte</strong> klinker? Dan is de lettergreep meestal{' '}
                  <strong data-id="element-14524" data-mp="7u02hxxukydy">gesloten</strong> en schrijf je{' '}
                  <strong data-id="element-14525" data-mp="cv7b1z7te1lo">twee medeklinkers</strong>. Hoor je een{' '}
                  <strong data-id="element-14526" data-mp="csbdwd3sjgya">lange</strong> klinker? Dan is de lettergreep{' '}
                  <strong data-id="element-14527" data-mp="2ej21onoz1rtr">open</strong> en schrijf je{' '}
                  <strong data-id="element-14528" data-mp="29kgzwju2n037">één medeklinker</strong>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}